import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

// Marge gardée entre la liste et le bord de la fenêtre
const MARGE = 12;

// Menu déroulant des régions : remplace le <select> natif, dont la liste ne se stylise pas.
// Clavier : flèches pour parcourir, Entrée / Espace pour choisir, Échap pour fermer.
// La liste s'ouvre vers le bas, ou vers le haut s'il y a plus de place au-dessus.
const ChoixRegion = ({ regions, valeur, onChange }) => {
    const [ouvert, setOuvert] = useState(false);
    const [active, setActive] = useState(0);
    const [position, setPosition] = useState({ versHaut: false, hauteurMax: undefined });
    const conteneur = useRef(null);
    const declencheur = useRef(null);
    const liste = useRef(null);

    // Mesure avant affichage : pas de clignotement dans le mauvais sens
    useLayoutEffect(() => {
        if (!ouvert) return;
        const bouton = declencheur.current.getBoundingClientRect();
        const hauteurListe = liste.current.scrollHeight;
        const dessous = window.innerHeight - bouton.bottom - MARGE;
        const dessus = bouton.top - MARGE;
        const versHaut = hauteurListe > dessous && dessus > dessous;
        setPosition({ versHaut, hauteurMax: versHaut ? dessus : dessous });
    }, [ouvert]);

    // Garde l'option active visible si la liste défile, sans faire défiler la page
    useEffect(() => {
        const el = liste.current?.children[active];
        if (!el) return;
        const ul = liste.current;
        if (el.offsetTop < ul.scrollTop) ul.scrollTop = el.offsetTop;
        else if (el.offsetTop + el.offsetHeight > ul.scrollTop + ul.clientHeight) {
            ul.scrollTop = el.offsetTop + el.offsetHeight - ul.clientHeight;
        }
    }, [active, ouvert]);

    useEffect(() => {
        if (!ouvert) return undefined;
        liste.current?.focus({ preventScroll: true });
        const fermerSiDehors = (e) => {
            if (!conteneur.current?.contains(e.target)) setOuvert(false);
        };
        document.addEventListener("mousedown", fermerSiDehors);
        return () => document.removeEventListener("mousedown", fermerSiDehors);
    }, [ouvert]);

    const ouvrir = () => {
        const index = regions.findIndex((r) => r.id === valeur?.id);
        setActive(index === -1 ? 0 : index);
        setOuvert(true);
    };

    const fermer = () => {
        setOuvert(false);
        declencheur.current?.focus();
    };

    const choisir = (region) => {
        onChange(region);
        fermer();
    };

    const handleTouche = (e) => {
        if (e.key === "ArrowDown") setActive((i) => Math.min(i + 1, regions.length - 1));
        else if (e.key === "ArrowUp") setActive((i) => Math.max(i - 1, 0));
        else if (e.key === "Home") setActive(0);
        else if (e.key === "End") setActive(regions.length - 1);
        else if (e.key === "Enter" || e.key === " ") choisir(regions[active]);
        else if (e.key === "Escape") fermer();
        else if (e.key === "Tab") setOuvert(false);
        else return;
        if (e.key !== "Tab") e.preventDefault();
    };

    return (
        <div className="choixRegion" ref={conteneur}>
            <button
                type="button"
                ref={declencheur}
                className={`btn btnBeige choixRegion-bouton${ouvert ? " ouvert" : ""}`}
                aria-haspopup="listbox"
                aria-expanded={ouvert}
                onClick={() => (ouvert ? setOuvert(false) : ouvrir())}
                onKeyDown={(e) => {
                    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                        e.preventDefault();
                        ouvrir();
                    }
                }}
            >
                <span>{valeur ? valeur.title : "Choisir une région"}</span>
                <span className="choixRegion-chevron" aria-hidden="true" />
            </button>
            {ouvert && (
                <ul
                    ref={liste}
                    className={`choixRegion-liste${position.versHaut ? " versHaut" : ""}`}
                    style={{ maxHeight: position.hauteurMax }}
                    role="listbox"
                    tabIndex={-1}
                    aria-label="Régions d'Astolie"
                    aria-activedescendant={`region-${regions[active].id}`}
                    onKeyDown={handleTouche}
                >
                    {regions.map((r, i) => (
                        <li
                            key={r.id}
                            id={`region-${r.id}`}
                            role="option"
                            aria-selected={r.id === valeur?.id}
                            className={i === active ? "active" : undefined}
                            onMouseEnter={() => setActive(i)}
                            onClick={() => choisir(r)}
                        >
                            {r.title}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default ChoixRegion;
